import { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';

import { SyntaxNode } from '@lezer/common';

import { UnexpectedError } from '@watershed/errors/UnexpectedError';
import { UserInputError } from '@watershed/errors/UserInputError';
import { i18n } from '@watershed/intl';

/**
 * @fileoverview Utility functions and classes for parsing DSL strings with Lezer
 */

/**
 * Error codes for parser errors
 */
export enum ParserErrorCode {
  NoTokensToParse = 'NoTokensToParse',
  InvalidNumber = 'InvalidNumber',
  UnterminatedStringLiteral = 'UnterminatedStringLiteral',
  InvalidBooleanValue = 'InvalidBooleanValue',
  UnexpectedToken = 'UnexpectedToken',
  UnexpectedTokensAfterExpression = 'UnexpectedTokensAfterExpression',
  ExpectedParentheses = 'ExpectedParentheses',
  EmptyParentheses = 'EmptyParentheses',
  EmptyArgumentInParentheses = 'EmptyArgumentInParentheses',
  InOperatorRequiresLiterals = 'InOperatorRequiresLiterals',
  InOperatorMixedTypes = 'InOperatorMixedTypes',
  NoMatchingRuleFound = 'NoMatchingRuleFound',
  ExpectedOneTopLevelExpression = 'ExpectedOneTopLevelExpression',
  DuplicateKeywordArgument = 'DuplicateKeywordArgument',
  UnsupportedFunction = 'UnsupportedFunction',
  SyntaxError = 'SyntaxError',
  MissingSelectClause = 'MissingSelectClause',
  DuplicateClause = 'DuplicateClause',
  EmptyClause = 'EmptyClause',
  ExpectedNonNegativeIntegerValue = 'ExpectedNonNegativeIntegerValue',
  SelectMustBeFirst = 'SelectMustBeFirst',
  ExpectedIdentifierAfterAs = 'ExpectedIdentifierAfterAs',
}

/**
 * Error messages for parser error codes
 */
const PARSER_ERROR_MESSAGES: Record<ParserErrorCode, MessageDescriptor> = {
  [ParserErrorCode.NoTokensToParse]: msg({
    message: 'No tokens to parse',
    context: 'Parser error message',
  }),
  [ParserErrorCode.InvalidNumber]: msg({
    message: 'Invalid number',
    context: 'Parser error message',
  }),
  [ParserErrorCode.UnterminatedStringLiteral]: msg({
    message: 'Unterminated string literal',
    context: 'Parser error message',
  }),
  [ParserErrorCode.InvalidBooleanValue]: msg({
    message: 'Invalid boolean value',
    context: 'Parser error message',
  }),
  [ParserErrorCode.UnexpectedToken]: msg({
    message: 'Unexpected token',
    context: 'Parser error message',
  }),
  [ParserErrorCode.UnexpectedTokensAfterExpression]: msg({
    message: 'Unexpected tokens after expression',
    context: 'Parser error message',
  }),
  [ParserErrorCode.ExpectedParentheses]: msg({
    message: 'Expected parentheses',
    context: 'Parser error message',
  }),
  [ParserErrorCode.EmptyParentheses]: msg({
    message: 'Unexpected empty parentheses',
    context: 'Parser error message',
  }),
  [ParserErrorCode.EmptyArgumentInParentheses]: msg({
    message: 'Empty argument in parentheses',
    context: 'Parser error message',
  }),
  [ParserErrorCode.InOperatorRequiresLiterals]: msg({
    message: 'IN operator requires literal values',
    context: 'Parser error message',
  }),
  [ParserErrorCode.InOperatorMixedTypes]: msg({
    message: 'All literals in IN operator must be the same type',
    context: 'Parser error message',
  }),
  [ParserErrorCode.NoMatchingRuleFound]: msg({
    message: 'No matching rule found',
    context: 'Parser error message',
  }),
  [ParserErrorCode.ExpectedOneTopLevelExpression]: msg({
    message: 'Expected exactly one top-level expression',
    context: 'Parser error message',
  }),
  [ParserErrorCode.UnsupportedFunction]: msg({
    message: 'Unsupported function',
    context: 'Parser error message',
  }),
  [ParserErrorCode.SyntaxError]: msg({
    message: 'Syntax error',
    context: 'Parser error message',
  }),
  [ParserErrorCode.DuplicateKeywordArgument]: msg({
    message: 'Duplicate keyword argument',
    context: 'Parser error message',
  }),
  [ParserErrorCode.MissingSelectClause]: msg({
    message: 'Query must include a SELECT clause',
    context: 'Parser error message',
  }),
  [ParserErrorCode.DuplicateClause]: msg({
    message: 'Duplicate clause in query',
    context: 'Parser error message',
  }),
  [ParserErrorCode.EmptyClause]: msg({
    message: 'Clause cannot be empty',
    context: 'Parser error message',
  }),
  [ParserErrorCode.ExpectedNonNegativeIntegerValue]: msg({
    message: 'Expected a non-negative integer value',
    context: 'Parser error message',
  }),
  [ParserErrorCode.SelectMustBeFirst]: msg({
    message: 'SELECT clause must come first in the query',
    context: 'Parser error message',
  }),
  [ParserErrorCode.ExpectedIdentifierAfterAs]: msg({
    message: 'Expected identifier after AS',
    context: 'Parser error message',
  }),
};

/**
 * A position in the parsed input string.
 */
export type ParserPosition = Readonly<{
  input: string;
  from: number;
  to: number;
}>;

/**
 * Enhanced parser error with error codes and context
 */
export class ParseError extends UserInputError {
  constructor(
    public readonly parseError: ParserErrorCode,
    public readonly context: string,
    public readonly position: { from: number; to: number }
  ) {
    super(i18n._(PARSER_ERROR_MESSAGES[parseError]));

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ParseError);
    }
    Object.setPrototypeOf(this, ParseError.prototype);
  }

  static isParseError(error: unknown): error is ParseError {
    return (
      error instanceof ParseError ||
      (error instanceof Error && 'parseError' in error)
    );
  }
}

/**
 * Context object for parser operations based on Lezer concrete syntax trees.
 *
 * For efficiency Lezer's parse tree only stores offsets into the input, and not
 * e.g., substrings of the input. In order to get at those strings (e.g., to get
 * the text of an identifier, or to render an error message for an expression)
 * we need to separately pass around the original string. This context object is
 * mostly a wrapper around the input string, but with some helpers for common
 * operations.
 */
export class ParseContext {
  static contextRadius = 15;
  static warningMarker = '❌';

  constructor(readonly input: string) {}

  /** Get the text of a node */
  slice(node: SyntaxNode): string {
    return this.input.slice(node.from, node.to);
  }

  /** Get the text of a node, as lowercase */
  sliceLower(node: SyntaxNode): string {
    return this.slice(node).toLowerCase();
  }

  /** Get the position of a node */
  position(node: SyntaxNode): ParserPosition {
    return {
      input: this.input,
      from: node.from,
      to: node.to,
    };
  }

  /**
   * Create an enhanced parser error with error code and context
   */
  error(
    /** The error code */
    code: ParserErrorCode,
    /**
     * The node or position of the error. Note that because SyntaxNode has a reflective
     * `node` property, we can pass a node directly as `opts`
     */
    opts: { node?: SyntaxNode; position?: number } = {}
  ): ParseError {
    const from = opts.node?.from ?? opts.position ?? 0;
    const to = opts.node?.to ?? from;
    const context = this.formatErrorContext(from);
    return new ParseError(code, context, { from, to });
  }

  /**
   * Abort parsing with an exception when a state we believe is
   * impossible happens. These exceptions are *our* fault as authors of this
   * file, and likely indicates that there's a difference in expectations
   * between this file and the parser grammar.
   *
   * These are different than ParseErrors, which represent bad input.
   */
  impossible(what: string, node: SyntaxNode): never {
    throw new UnexpectedError(`impossible: bad ${what}: ${this.debug(node)}`);
  }

  /**
   * Get debug information about a node
   */
  debug(node: SyntaxNode): string {
    const text = this.slice(node);
    return `${text} (type=${node.name}, from=${node.from}, to=${node.to})`;
  }

  /**
   * Create a formatted error context string with the error position marked
   */
  formatErrorContext(position: number): string {
    // Handle empty input
    if (this.input.length === 0) {
      return `${ParseContext.warningMarker}(empty input)`;
    }

    const start = Math.max(0, position - ParseContext.contextRadius);
    const end = Math.min(
      this.input.length,
      position + ParseContext.contextRadius
    );

    let context = this.input.slice(start, end);
    const relativePos = position - start;

    // Add ellipsis if we truncated
    let posOffset = 0;
    if (start > 0) {
      context = '...' + context;
      posOffset += 3;
    }
    if (end < this.input.length) {
      context = context + '...';
    }

    // Insert warning sign at error position
    const adjustedPos = relativePos + posOffset;
    if (adjustedPos <= context.length) {
      context =
        context.slice(0, adjustedPos) +
        ParseContext.warningMarker +
        context.slice(adjustedPos);
    }

    return context;
  }
}

/**
 * Get the children of a node. Lezer has a less-than-ergonomic tree representation
 * (for efficiency). This helper converts things into a more normal array-of-children
 * representation.
 */
export function getNodeChildren(node: SyntaxNode): Array<SyntaxNode> {
  const out = [];
  let child = node.firstChild;
  while (child) {
    out.push(child);
    child = child.nextSibling;
  }
  return out;
}
